import type { AttemptInput } from '@dcs/contracts';
import type { KeyValueStorage } from '@dcs/platform/ports';
import { createWebStorage } from '@dcs/platform/web';
import { createAttemptQueue, type AttemptQueue } from '../queue';

/**
 * What the rest of the app may call without being inside the save provider (F6 T-2.2):
 *
 * - `subscribeAttemptSaved(listener)`: runs after the server accepted an attempt sent from this tab
 *   (also when the answer was a 200 replay). The history list (T-2.3) uses it to invalidate its query
 *   cache, so a result sent after reconnecting shows up without a reload. It does not fire for an
 *   attempt another tab sent (that list refetches on focus like any query).
 * - `purgeAttemptQueue(owner?)`: forgets the queued results, dead letters and the "last signed-in
 *   user" hint of `owner` (everything when omitted). The sign-out path calls it for you; the
 *   account-deletion flow (T-2.5) must call it after the server confirmed the deletion.
 */

/** Id of the user last seen signed in on this device (see `AttemptSaveCore`). */
export const OWNER_HINT_KEY = 'dcs.attempts.owner';

const savedListeners = new Set<(attempt: AttemptInput) => void>();

export function subscribeAttemptSaved(listener: (attempt: AttemptInput) => void): () => void {
  savedListeners.add(listener);
  return () => void savedListeners.delete(listener);
}

export function emitAttemptSaved(attempt: AttemptInput): void {
  for (const listener of [...savedListeners]) listener(attempt);
}

interface Active {
  readonly queue: AttemptQueue;
  readonly storage: KeyValueStorage;
}
let active: Active | null = null;

/** The provider registers its queue here so `purgeAttemptQueue` reaches the one the UI watches. */
export function registerAttemptQueue(next: Active | null): void {
  active = next;
}

/** Purges `owner` from `queue` and `storage`, hint included (all owners when omitted). */
export function purgeOwner(queue: AttemptQueue, storage: KeyValueStorage, owner?: string): void {
  queue.purge(owner);
  if (owner === undefined || storage.get(OWNER_HINT_KEY) === owner) storage.remove(OWNER_HINT_KEY);
}

export function purgeAttemptQueue(owner?: string): void {
  if (active !== null) {
    purgeOwner(active.queue, active.storage, owner);
    return;
  }
  const storage = createWebStorage();
  const queue = createAttemptQueue({
    storage,
    send: () => Promise.reject(new Error('purge only')),
  });
  purgeOwner(queue, storage, owner);
}
